
import React, { useState } from 'react';
import './DatabasePage.css';
import PageHeader from '../../components/shared/PageHeader';
import Card from '../../components/shared/Card';
import Button from '../../components/shared/Button';
import Badge from '../../components/shared/Badge';
import { useToast } from '../../context/ToastContext';

const TABLES = [
  { name: 'users', rows: 12840, size: '4.2 MB', cols: 14 },
  { name: 'contacts', rows: 8312, size: '2.8 MB', cols: 22 },
  { name: 'invoices', rows: 4721, size: '1.4 MB', cols: 18 },
  { name: 'sessions', rows: 94210, size: '18.3 MB', cols: 9 },
  { name: 'audit_log', rows: 182940, size: '42.1 MB', cols: 11 },
  { name: 'integrations', rows: 248, size: '0.1 MB', cols: 16 },
];

const SAMPLE_ROWS = {
  users: [
    { id: 1, email: 'jane@superdash.io', role: 'admin', created: '2024-01-15', status: 'active' },
    { id: 2, email: 'sarah@acme.com', role: 'member', created: '2024-03-22', status: 'active' },
    { id: 3, email: 'james@bluesky.io', role: 'viewer', created: '2024-06-10', status: 'inactive' },
    { id: 4, email: 'elena@meridian.co', role: 'member', created: '2025-01-08', status: 'active' },
  ],
  contacts: [
    { id: 1, name: 'Sarah Mitchell', company: 'Acme Corp', status: 'active', value: 12400 },
    { id: 2, name: 'James Reeves', company: 'BlueSky Inc', status: 'lead', value: 5800 },
    { id: 3, name: 'Elena Vasquez', company: 'Meridian Labs', status: 'active', value: 28100 },
  ],
};

const STARTER_QUERIES = [
  { label: 'All active users', sql: 'SELECT * FROM users WHERE status = \'active\' ORDER BY created DESC;' },
  { label: 'Top contacts by value', sql: 'SELECT name, company, value FROM contacts ORDER BY value DESC LIMIT 10;' },
  { label: 'Invoice summary', sql: 'SELECT status, COUNT(*) as count, SUM(amount) as total FROM invoices GROUP BY status;' },
  { label: 'Recent sessions', sql: 'SELECT user_id, started_at, duration FROM sessions ORDER BY started_at DESC LIMIT 20;' },
];

export default function DatabasePage() {
  const [selectedTable, setSelectedTable] = useState('users');
  const [query, setQuery] = useState('SELECT * FROM users WHERE status = \'active\' ORDER BY created DESC;');
  const [queryResult, setQueryResult] = useState<any[] | null>(null);
  const [queryError, setQueryError] = useState('');
  const [running, setRunning] = useState(false);
  const [activeTab, setActiveTab] = useState<'tables' | 'query'>('tables');
  const { addToast } = useToast();

  const runQuery = async () => {
    if (!query.trim()) return;
    setRunning(true);
    setQueryError('');
    setQueryResult(null);
    await new Promise(r => setTimeout(r, 700));

    const lower = query.toLowerCase().trim();
    if (lower.includes('drop') || lower.includes('delete') || lower.includes('truncate')) {
      setQueryError('Error: Destructive queries are not permitted in this environment.');
      setRunning(false);
      return;
    }

    const tableMatch = Object.keys(SAMPLE_ROWS).find(t => lower.includes(t));
    if (tableMatch) {
      setQueryResult((SAMPLE_ROWS as any)[tableMatch]);
    } else {
      setQueryResult([{ result: 'Query executed successfully', rows_affected: Math.floor(Math.random() * 20 + 1), time: `${Math.floor(Math.random() * 80 + 10)}ms` }]);
    }
    setRunning(false);
    addToast('success', `Query executed in ${Math.floor(Math.random() * 80 + 10)}ms`);
  };

  const rows = queryResult;
  const cols = rows && rows.length > 0 ? Object.keys(rows[0]) : [];

  return (
    <div className="database-page">
      <PageHeader
        title="Database"
        breadcrumb={['Ops', 'Database']}
        actions={
          <>
            <Badge variant="success">Postgres 16</Badge>
            <Button variant="ghost" size="sm" onClick={() => addToast('info', 'Opening migration tool…')}>Migrations</Button>
          </>
        }
      />

      {/* Tab bar */}
      <div className="db-tabs">
        <button className={`db-tab${activeTab === 'tables' ? ' db-tab--active' : ''}`} onClick={() => setActiveTab('tables')}>📋 Tables</button>
        <button className={`db-tab${activeTab === 'query' ? ' db-tab--active' : ''}`} onClick={() => setActiveTab('query')}>💻 Query Editor</button>
      </div>

      {activeTab === 'tables' && (
        <div className="db-layout">
          {/* Table list */}
          <Card title="Tables" className="db-table-list">
            {TABLES.map(t => (
              <button
                key={t.name}
                className={`db-table-btn${selectedTable === t.name ? ' db-table-btn--active' : ''}`}
                onClick={() => setSelectedTable(t.name)}
              >
                <span className="db-table-icon">🗃</span>
                <div className="db-table-info">
                  <span className="db-table-name">{t.name}</span>
                  <span className="db-table-meta">{t.rows.toLocaleString()} rows · {t.size}</span>
                </div>
                <Badge variant="default">{t.cols} cols</Badge>
              </button>
            ))}
          </Card>

          {/* Table preview */}
          <Card
            title={`Preview: ${selectedTable}`}
            className="db-preview-card"
            actions={
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button size="sm" variant="ghost" onClick={() => { setQuery(`SELECT * FROM ${selectedTable} LIMIT 100;`); setActiveTab('query'); }}>Open in Editor</Button>
                <Button size="sm" onClick={() => addToast('info', 'Exporting table…')}>Export CSV</Button>
              </div>
            }
          >
            <div className="db-table-wrap">
              <table className="db-table">
                <thead>
                  <tr>
                    {Object.keys(((SAMPLE_ROWS as any)[selectedTable] || SAMPLE_ROWS.users)[0] || {}).map(col => (
                      <th key={col}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {((SAMPLE_ROWS as any)[selectedTable] || SAMPLE_ROWS.users).map((row: any, i: number) => (
                    <tr key={i}>
                      {Object.values(row).map((val, j) => (
                        <td key={j}>{String(val)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="db-table-footer">
              Showing {((SAMPLE_ROWS as any)[selectedTable] || SAMPLE_ROWS.users).length} of {TABLES.find(t => t.name === selectedTable)?.rows.toLocaleString()} rows
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'query' && (
        <div className="db-query-layout">
          {/* Query starter templates */}
          <div className="db-query-starters">
            {STARTER_QUERIES.map(q => (
              <button key={q.label} className="db-starter-btn" onClick={() => setQuery(q.sql)}>
                {q.label}
              </button>
            ))}
          </div>

          {/* Editor */}
          <Card title="SQL Editor" actions={
            <Button size="sm" onClick={runQuery} disabled={running}>
              {running ? '⏳ Running…' : '▶ Run Query'}
            </Button>
          }>
            <textarea
              className="db-query-input"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) runQuery();
              }}
              placeholder="SELECT * FROM users;"
              spellCheck={false}
            />
            <div className="db-editor-footer">
              <span>Press ⌘/Ctrl+Enter to run</span>
              <span>{query.split('\n').length} lines</span>
            </div>
          </Card>

          {/* Results */}
          {queryError && (
            <Card title="Error">
              <div className="db-error">{queryError}</div>
            </Card>
          )}
          {rows && rows.length > 0 && (
            <Card title={`Results — ${rows.length} row${rows.length !== 1 ? 's' : ''}`} actions={<Button size="sm" variant="ghost" onClick={() => addToast('success', 'Results exported!')}>Export CSV</Button>}>
              <div className="db-table-wrap">
                <table className="db-table">
                  <thead>
                    <tr>{cols.map(c => <th key={c}>{c}</th>)}</tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr key={i}>
                        {cols.map(c => <td key={c}>{String(row[c])}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
